import React, { useEffect, useState } from 'react';
import { PersonalInfoWithId } from '../../types/forms';
import { 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import AddressAutocomplete from '../common/AddressAutocomplete';
import { PhoneField } from '../common/ValidatedFields';
import axios from 'axios';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

interface PersonalInformationTabProps {
  formData: PersonalInfoWithId;
  onChange: (data: PersonalInfoWithId) => void;
  onNext: () => void;
  onSave: () => void;
  saveStatus: 'saved' | 'error' | null;
}

const PersonalInformationTab = React.forwardRef<HTMLDivElement, PersonalInformationTabProps>((props, ref) => {
  const { formData, onChange, onNext, onSave, saveStatus } = props;
  
  console.log('PersonalInformationTab received formData:', formData);
  
  // State for gender options
  const [genderOptions, setGenderOptions] = useState<{value: string, displayName: string}[]>([]);
  const [genderLoading, setGenderLoading] = useState(false);
  
  // Fetch gender options from lookups
  useEffect(() => {
    const fetchGenderOptions = async () => {
      setGenderLoading(true);
      try {
        const response = await axios.get('/api/lookups/public/gender');
        if (response.data && Array.isArray(response.data)) {
          setGenderOptions(response.data);
        }
      } catch (error) {
        console.error('Error fetching gender options:', error);
      } finally {
        setGenderLoading(false);
      }
    };
    
    fetchGenderOptions();
  }, []);

  // Update local state when formData changes
  React.useEffect(() => {
    console.log('formData changed in PersonalInformationTab:', formData);
  }, [formData]);

  // Calculate age from date of birth
  const calculateAge = (dob: string | null): number | null => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleDateOfBirthChange = (date: dayjs.Dayjs | null) => {
    if (date && date.isValid()) {
      const formattedDate = date.format('YYYY-MM-DD');
      const age = calculateAge(formattedDate);
      onChange({
        ...formData,
        dob: formattedDate,
        age: age || undefined
      });
    } else {
      onChange({
        ...formData,
        dob: '',  
        age: undefined
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  return (
    <div ref={ref}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Personal Details
            </Typography>
          </Grid>

          {/* Pre-populated, Mandatory, Editable: Yes */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData?.firstName ?? ''}
              onChange={(e) => onChange({ ...formData, firstName: e.target.value })}
              required
            />
          </Grid>

          {/* Pre-populated, Mandatory, Editable: Yes */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData?.lastName ?? ''}
              onChange={(e) => onChange({ ...formData, lastName: e.target.value })}
              required
            />
          </Grid>

          {/* Pre-populated, Mandatory, Editable: No */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData?.email ?? ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{ '& .MuiInputBase-input.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
              required
            />
          </Grid>

          {/* Pre-populated, Mandatory, Editable: Yes, Phone validation */}
          <Grid item xs={12} sm={6}>
            <PhoneField
              value={formData?.phone ?? ''}
              onChange={(value) => onChange({ ...formData, phone: value })}
              label="Phone Number"
              required
            />
          </Grid>

          {/* Date of Birth Field */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
              <DatePicker
                label="Date of Birth"
                value={formData.dob ? dayjs(formData.dob) : null}
                onChange={handleDateOfBirthChange}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    inputProps: {
                      placeholder: 'DD/MM/YYYY'
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Calculated Age - Read Only */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Age"
              value={formData.dob ? (calculateAge(formData.dob) || '') : ''}
              InputProps={{
                readOnly: true,
              }}
              sx={{ '& .MuiInputBase-input.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
            />
          </Grid>

          {/* Gender Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="gender-select-label">Gender</InputLabel>
              <Select
                labelId="gender-select-label"
                id="gender-select"
                value={formData.gender || ''}
                label="Gender"
                onChange={(e) => onChange({ ...formData, gender: e.target.value })}
                disabled={genderLoading}
              >
                <MenuItem value=""><em>Select Gender</em></MenuItem>
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Address Autocomplete */}
          <Grid item xs={12}>
            <AddressAutocomplete
              value={{
                addressLine1: formData.addressLine1 || '',
                addressLine2: formData.addressLine2 || '',
                addressLine3: formData.addressLine3 || '',
                city: formData.city || '',
                county: formData.county || '',
                postcode: formData.postcode || '',
                country: formData.country || ''
              }}
              onChange={(address) => {
                console.log('AddressAutocomplete onChange called with:', address);
                const updatedData = {
                  ...formData,
                  addressLine1: address.addressLine1 || '',
                  addressLine2: address.addressLine2 || '',
                  addressLine3: address.addressLine3 || '',
                  city: address.city || '',
                  county: address.county || '',
                  postcode: address.postcode || '',
                  country: address.country || ''
                };
                console.log('Updated formData with address:', updatedData);
                onChange(updatedData);
              }}
              fieldMapping={{
                line1: 'addressLine1',
                line2: 'addressLine2',
                line3: 'addressLine3',
                city: 'city',
                postcode: 'postcode',
                county: 'county',
                country: 'country'
              }}
              required={true}
              label="Home Address"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onSave}
              >
                Save Progress
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                Next
              </Button>
            </Box>
          </Grid>

          {saveStatus === 'saved' && (
            <Grid item xs={12}>
              <Alert severity="success">Progress saved successfully!</Alert>
            </Grid>
          )}
          
          {saveStatus === 'error' && (
            <Grid item xs={12}>
              <Alert severity="error">Error saving progress. Please try again.</Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </div>
  );
});

PersonalInformationTab.displayName = 'PersonalInformationTab';

export default PersonalInformationTab;